/**
 * useFocusTrap hook
 * Traps keyboard focus within a container element when active.
 * Returns focus to the previously focused element when deactivated.
 *
 * ⚠️ NESTING. The Tab listener lives on `document`, so with two overlays open
 * BOTH traps used to run on every key press. The outer one sees the focus
 * sitting inside the inner dialog — which is not in ITS container — takes the
 * `!container.contains(active)` branch, and yanks focus back to its own first
 * element. The inner trap does the same. The two cancel out and focus is PINNED
 * on one control: Tab stops advancing entirely.
 *
 * Measured in the browser (2026-08-11, /transactions → «Acciones Masivas» →
 * «Eliminar por Medio» → confirm): ten consecutive Tabs all landed on the same
 * close button, so the mandatory «Entiendo que esto retira del libro…» checkbox
 * could NEVER be reached — and with it unticked the confirm button stays
 * disabled. The whole bulk delete was impossible with a keyboard.
 *
 * Only the TOPMOST trap handles Tab. `useNestedOverlayEscape` already draws
 * exactly this line for Escape; this is the same rule for Tab.
 */

/**
 * Containers with an ACTIVE trap, in activation order. The last one is the
 * overlay actually on top, and the only one that may move focus.
 */
import { useEffect, RefObject } from 'react'

const activeTrapContainers: HTMLElement[] = []

const FOCUSABLE_SELECTORS = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

export function useFocusTrap(ref: RefObject<HTMLElement | null>, isActive: boolean): void {
  useEffect(() => {
    if (!isActive || !ref.current) return

    const container = ref.current

    // Claim the top of the stack for as long as this trap is mounted+active.
    activeTrapContainers.push(container)

    // Remember what was focused before the trap activated
    const previouslyFocused = document.activeElement as HTMLElement | null

    const getFocusableElements = (): HTMLElement[] => {
      return Array.from(container.querySelectorAll(FOCUSABLE_SELECTORS)).filter(
        (el: Element): el is HTMLElement => {
          return el instanceof HTMLElement && !el.closest('[hidden]') && el.offsetParent !== null
        },
      )
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      // Not the overlay on top ⇒ stay out of the way. Without this the outer
      // trap fights the inner one for the focus and Tab never advances.
      if (activeTrapContainers[activeTrapContainers.length - 1] !== container) return

      const focusable = getFocusableElements()
      if (focusable.length === 0) {
        event.preventDefault()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement as HTMLElement

      if (event.shiftKey) {
        // Shift+Tab: going backward
        if (active === first || !container.contains(active)) {
          event.preventDefault()
          last.focus()
        }
      } else {
        // Tab: going forward
        if (active === last || !container.contains(active)) {
          event.preventDefault()
          first.focus()
        }
      }
    }

    // Focus the container (or first focusable element) when trap activates
    const focusable = getFocusableElements()
    if (focusable.length > 0) {
      focusable[0].focus()
    } else {
      container.focus()
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      const index = activeTrapContainers.lastIndexOf(container)
      if (index !== -1) activeTrapContainers.splice(index, 1)
      document.removeEventListener('keydown', handleKeyDown)
      // Return focus to the element that was focused before the trap activated
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus()
      }
    }
  }, [isActive, ref])
}
